import { useState, useMemo } from 'react';
import { journeys as journeysApi } from '../../../apiService/blustreamApi';
import { useTouchpointMutation, useAttributeOptions, useLabelOptions } from './touchpointHooks';
import { toIsoDuration, parseWaitTime } from '../../../utility/isoDuration';
import { isValidLinkUrl } from '../../../utility/linkValidators';

const EMPTY = { name: '', message: '', link: '', waitValue: 0, waitUnit: 'days', sentAttr: null, respAttr: null, linkAttr: null };

// Pure: which label uuids were added / removed between original and selected.
export function diffLabels(original, selected) {
  const originalSet = new Set(original); const selectedSet = new Set(selected);
  return { added: selected.filter((uuid) => !originalSet.has(uuid)), removed: original.filter((uuid) => !selectedSet.has(uuid)) };
}

export function formFromTouchpoint(tp) {
  if (!tp) return { ...EMPTY };
  const wait = parseWaitTime(tp.wait_time);
  return {
    name: tp.name || '', message: tp.message || '', link: tp.link || '',
    waitValue: wait.value, waitUnit: wait.unit,
    sentAttr: tp.touchpoint_sent_attribute_id || null,
    respAttr: tp.response_attribute_id || null,
    linkAttr: tp.link_click_attribute_id || null,
  };
}

// Saved touchpoint overlaid with pending edits — used to render the live preview card.
// Temp id for the not-yet-saved touchpoint's live-preview card (real ids are positive).
export const NEW_TP_ID = -1;

// Live-preview card for create mode: a phantom touchpoint that mirrors the form so the
// user can see & reposition it in the timeline before saving. `__isNew` tells the card to
// render placeholders and hide metrics.
export function buildCreatePreview(form, journeyId) {
  const linkValid = isValidLinkUrl(form.link);
  return {
    touchpoint_id: NEW_TP_ID, journey_id: journeyId, __isNew: true, labels: [],
    name: form.name, message: form.message,
    wait_time: toIsoDuration(form.waitValue, form.waitUnit),
    link: linkValid ? (form.link || null) : null,
    response_attribute_id: form.respAttr || null,
    touchpoint_sent_attribute_id: form.sentAttr || null,
    link_click_attribute_id: form.linkAttr || null,
  };
}

export function mergePreviewTouchpoint(touchpoint, form) {
  if (!touchpoint) return null;
  const linkValid = isValidLinkUrl(form.link);
  return {
    ...touchpoint,
    name: form.name, message: form.message,
    wait_time: toIsoDuration(form.waitValue, form.waitUnit),
    link: linkValid ? (form.link || null) : (touchpoint.link || null),
    response_attribute_id: form.respAttr || null,
    touchpoint_sent_attribute_id: form.sentAttr || null,
    link_click_attribute_id: form.linkAttr || null,
  };
}

export function buildTouchpointBody({ mode, form, sortedSiblings, posIndex, touchpoint, linkValid }) {
  if (mode === 'create') {
    const body = { name: form.name, wait_time: toIsoDuration(form.waitValue, form.waitUnit) };
    if (form.message.trim() !== '') body.message = form.message;
    if (linkValid && form.link) body.link = form.link;
    if (form.respAttr) body.response_attribute_id = form.respAttr;
    if (form.sentAttr) body.touchpoint_sent_attribute_id = form.sentAttr;
    if (form.linkAttr) body.link_click_attribute_id = form.linkAttr;
    // Insert at the slot the user picked: send the ordinal currently occupying it
    // (backend resequences the range). posIndex === length means "append" → omit ordinal.
    const at = sortedSiblings[posIndex];
    if (at) body.ordinal = at.ordinal;
    return body;
  }
  const body = {
    name: form.name, message: form.message, wait_time: toIsoDuration(form.waitValue, form.waitUnit),
    response_attribute_id: form.respAttr || '', touchpoint_sent_attribute_id: form.sentAttr || '', link_click_attribute_id: form.linkAttr || '',
  };
  const at = sortedSiblings[posIndex];
  const ord = at ? at.ordinal : (touchpoint ? touchpoint.ordinal : undefined);
  if (ord != null) body.ordinal = ord;
  if (linkValid) body.link = form.link || '';
  return body;
}

const seedPos = (isCreate, sortedSiblings, touchpoint, afterTouchpointId) => {
  if (isCreate) {
    // Seed to the slot AFTER the clicked "+ add after" touchpoint; append if none/not found.
    const i = sortedSiblings.findIndex((sibling) => sibling.touchpoint_id === afterTouchpointId);
    return i >= 0 ? i + 1 : sortedSiblings.length;
  }
  return Math.max(0, sortedSiblings.findIndex((sibling) => touchpoint && sibling.touchpoint_id === touchpoint.touchpoint_id));
};
const labelsOf = (tp) => (tp && tp.labels ? tp.labels : []).map((label) => label.uuid);
const sameKey = (form, pos, labels) => JSON.stringify([form, pos, [...labels].sort()]);

// Single source of truth for the touchpoint editor's pending state. Lifted to
// JourneyTimeline so both the (controlled) drawer and the live preview read from it.
export function useTouchpointEditor({ open, mode, touchpoint, siblings = [], journeyId, afterTouchpointId = null }) {
  const [form, setForm] = useState(EMPTY);
  const [posIndex, setPosIndex] = useState(0);
  const [labelUuids, setLabelUuids] = useState([]);
  const mutation = useTouchpointMutation(journeyId);
  const { data: attrOptions = [] } = useAttributeOptions();
  const { data: labelOptions = [] } = useLabelOptions();

  const isCreate = mode === 'create';
  const sortedSiblings = useMemo(() => [...siblings].sort((a, b) => a.ordinal - b.ordinal), [siblings]);
  const stashKey = touchpoint ? `tp-link-invalid:${touchpoint.touchpoint_id}` : null;

  // Seed the form/position/labels SYNCHRONOUSLY during render (React's "adjust state
  // when a prop changes" pattern) rather than in an effect. An effect seeds one frame
  // late, so the first render carries the stale posIndex (0) — which places a create
  // phantom at the top for a frame, and the follow-scroll (a layout effect) fires on that
  // wrong position and animates to the top. Seeding here means the first COMMITTED render
  // already has the right index. Re-seeds only on an open transition or a target/mode/
  // insertion-point change — deliberately NOT on every `siblings` change, so reordering
  // mid-edit doesn't reset the form/position.
  const [seededKey, setSeededKey] = useState(null);
  const openKey = open ? `${mode}|${touchpoint ? touchpoint.touchpoint_id : 'new'}|${afterTouchpointId}|${stashKey}` : null;
  if (!open && seededKey !== null) {
    setSeededKey(null); // drawer closed → forget, so the next open always re-seeds
  } else if (open && openKey !== seededKey) {
    setSeededKey(openKey);
    const base = formFromTouchpoint(touchpoint);
    let stashed = null;
    try { stashed = stashKey ? window.localStorage.getItem(stashKey) : null; } catch (e) { /* ignore */ }
    setForm(stashed != null ? { ...base, link: stashed } : base);
    setPosIndex(seedPos(isCreate, sortedSiblings, touchpoint, afterTouchpointId));
    setLabelUuids(labelsOf(touchpoint));
  }

  const set = (key) => (value) => setForm((prevForm) => ({ ...prevForm, [key]: value }));
  const move = (delta) => setPosIndex((i) => Math.max(0, Math.min(isCreate ? sortedSiblings.length : sortedSiblings.length - 1, i + delta)));

  const linkValid = isValidLinkUrl(form.link);
  const canSave = form.name.trim() !== '' && (form.message.trim() !== '' || (form.link && linkValid));
  const stepCount = isCreate ? sortedSiblings.length + 1 : Math.max(sortedSiblings.length, 1);

  const seedKey = sameKey(formFromTouchpoint(touchpoint), seedPos(isCreate, sortedSiblings, touchpoint, afterTouchpointId), labelsOf(touchpoint));
  const dirty = sameKey(form, posIndex, labelUuids) !== seedKey;

  const previewTouchpoint = (mode === 'edit' && touchpoint)
    ? mergePreviewTouchpoint(touchpoint, form)
    : (isCreate ? buildCreatePreview(form, journeyId) : null);

  const save = (onDone, onError) => {
    try {
      if (stashKey) {
        if (form.link && !linkValid) window.localStorage.setItem(stashKey, form.link);
        else window.localStorage.removeItem(stashKey);
      }
    } catch (e) { /* ignore */ }
    const body = buildTouchpointBody({ mode, form, sortedSiblings, posIndex, touchpoint, linkValid });
    mutation.mutate(
      { mode, touchpointId: touchpoint ? touchpoint.touchpoint_id : undefined, body },
      { onSuccess: async () => {
        if (mode === 'edit' && touchpoint) {
          const { added, removed } = diffLabels(labelsOf(touchpoint), labelUuids);
          const tid = touchpoint.touchpoint_id;
          await Promise.all([
            ...added.map((uuid) => journeysApi.attachTouchpointLabel(journeyId, tid, uuid)),
            ...removed.map((uuid) => journeysApi.detachTouchpointLabel(journeyId, tid, uuid)),
          ]);
        }
        onDone && onDone();
      },
      onError: (err) => onError && onError(err) },
    );
  };

  return {
    values: form, set, posIndex, move, stepCount,
    labelUuids, setLabelUuids, dirty, canSave, linkValid, isCreate,
    previewTouchpoint, targetIndex: posIndex,
    attrOptions, labelOptions, saving: mutation.isLoading, save,
  };
}
